import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import "../../../Main/Main.css";
import Cookies from "universal-cookie";
import PageTemplate from "../../../Templates/PageTemplate";
import { useNavigate } from "react-router";
import middleware from "../../../Api/Middleware";
import { useTable } from "react-table";
import TablePreset from "../../../Table/TablePreset";
import SortableHeader from "../../../Table/TableComponents/SortableHeader";
import CheckboxHeader from "../../../Table/TableComponents/CheckboxHeader";
import CheckboxCell from "../../../Table/TableComponents/CheckboxCell";
import "./CategorySettings.css";
import ToastError from "../../../Toasts/ToastError";
import Filters from "../../../Filters/Filters";
import subCategoryIcon from '../../../Main/Icons/90angle.png';
import { BackButton } from "../../../HouseStyle/Components/Buttons";

const CategorySettings = () => {
  const navigate = useNavigate();
  const cookies = new Cookies();
  const defaultFilters = [
    {
      index: 0,
      order: 1,
      value: 1,
      name: "Status categorie",
      field: "status",
      type: "radio",
      options: [
        { name: "Gepubliceerd", value: 1 },
        { name: "Gearchiveerd", value: 0 },
      ],
    },
  ];
  const [tempfilters, setTempFilters] = useState(defaultFilters);
  const [filters, setFilters] = useState(defaultFilters);
  const [categories, setCategories] = useState([]);
  const [order, setOrder] = useState({
    orderBy: "sorting_order",
    direction: "asc",
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchengine, setSearchengine] = useState("");
  const [pagination, setPagination] = useState({
    page_size: 10,
    page: 1,
    max_items: 0,
    begin_of_page: 0,
    end_of_page: 0,
  });
  const [showFilterModal, setShowFilterModal] = useState(false);

  const CloseFilterModal = () => {
    setShowFilterModal(false);
  };

  const fetchAll = async () => {
    try {
      const normalizedSearch = searchengine.trim();
      const searchQuery = normalizedSearch
        ? `&search=${encodeURIComponent(normalizedSearch)}&noSearch=[category_id,image,description,status,head_category_id,sorting_order,updated_at,created_at,updater_user_id,updater_user_id]`
        : "";

      const [categoriesRes] = await Promise.all([
        middleware.get(
          `/products/categoriesRecursive?page=${pagination.page}&page_size=${pagination.page_size}&sort_order=${order.direction || "asc"}&sort_by=${order.orderBy || "sorting_order"}${searchQuery}`
        ),
      ]);

      // Apply client-side filtering
      let filteredData = categoriesRes.data.data;
      const activeFilters = filters.filter((e) => e.value != null && e.value !== "");
      
      if (activeFilters.length > 0) {
        filteredData = filterCategoriesRecursive(filteredData, activeFilters);
      }

      setPagination(prev => ({
        ...prev,
        max_items: filteredData.length,
        begin_of_page: filteredData.length > 0 ? 1 : 0,
        end_of_page: Math.min(filteredData.length, pagination.page_size),
      }));
      setCategories(filteredData);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401) {
        cookies.remove("token", { path: "/" });
        navigate("/");
      }
      setLoading(false);
    }
  };

  const filterCategoriesRecursive = (categories, activeFilters) => {
    return categories.filter(category => {
      const matchesFilters = activeFilters.every(filter => {
        return String(category[filter.field]) === String(filter.value);
      });

      if (category.children && category.children.length > 0) {
        category.children = filterCategoriesRecursive(category.children, activeFilters);
      }

      return matchesFilters;
    });
  };

  useEffect(() => {
    fetchAll();
  }, [order, searchengine, pagination.page, pagination.page_size, filters]);

  const getAllSubCategoryIds = (category) => {
    let ids = [];
    if (category?.children?.length) {
      category.children.forEach(child => {
        ids.push(child.category_id);
        ids = ids.concat(getAllSubCategoryIds(child));
      });
    }
    return ids;
  };

  const flattenCategories = (categories) => {
    const flattened = [];

    const flatten = (items) => {
      items.forEach(item => {
        flattened.push(item);
        if (item.children && item.children.length > 0) {
          flatten(item.children);
        }
      });
    };

    flatten(categories);
    return flattened;
  };

  const flattenedCategories = useMemo(() => flattenCategories(categories), [categories]);

  const handleSelection = useCallback((Categoryid, isSelected) => {
    const category = flattenedCategories.find(cat => cat.category_id === Categoryid);
    const descendantIds = category ? getAllSubCategoryIds(category) : [];

    if (isSelected) {
      setSelectedIds((previous) => {
        const updated = new Set([...previous, Categoryid, ...descendantIds]);
        return Array.from(updated);
      });
    } else {
      const idsToRemove = new Set([Categoryid, ...descendantIds]);
      setSelectedIds((previous) => previous.filter((id) => !idsToRemove.has(id)));
    }
  }, [flattenedCategories, getAllSubCategoryIds]);

  const openCategory = (id) => {
    navigate(`/algemene/instellingen/product/categorieën/bewerken/${id}`);
  };

  // Recursive Accordion Component for categories
  const CategoryAccordion = ({ category, layer, selectedIds, handleSelection }) => {
    const subcategories = category.children || [];

    return (
      <div>
        <div className="d-flex align-items-center py-2" onClick={(e) => e.stopPropagation()}>
          <CheckboxCell
            row={{ original: category }}
            handleSelection={handleSelection}
            selectedIds={selectedIds}
            pk={"category_id"}
          />
          <div
            className="ms-2 text-info fs-5"
            style={{ paddingLeft: Math.max(0, layer - 1) * 30 + "px", cursor: 'pointer' }}
            onClick={() => openCategory(category.category_id)}
          >
            {category.head_category_id && (
              <img
                src={subCategoryIcon}
                width="25px"
                height="18px"
                className="me-2 bluefiltered"
                  style={{ marginBottom: "10px" }}
                alt="sub-category"
              />
            )}
            {category.title}
          </div>
          <div className="ms-auto" style={{ color: category.status == "1" ? "#A9D974" : "#ffcc9d" }}>
            {category.status == "1" ? "Gepubliceerd" : "Gearchiveerd"}
          </div>
        </div>

        {subcategories.length > 0 &&
          subcategories.map((subcategory, index) => (
            <CategoryAccordion
              key={subcategory.category_id || index}
              category={subcategory}
              layer={layer + 1}
              selectedIds={selectedIds}
              handleSelection={handleSelection}
            />
          ))}
      </div>
    );
  };

  const columns = useMemo(() => {
    const allCategories = flattenedCategories;

    return [

      {
        Header: () => (
          <div className="d-flex justify-content-between">
            <div className="pe-5 catHeaderCheckbox">
              <CheckboxHeader
                currentItem={allCategories}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                pk={"category_id"}
              />
            </div>
            <div className="d-flex w-100 justify-content-between">
              <SortableHeader
                currentItem={order}
                setItem={setOrder}
                field={"title"}
                pos={"start"}
              >
                Categorie
              </SortableHeader>
              <SortableHeader
                currentItem={order}
                setItem={setOrder}
                field={"status"}
                pos={"end"}
              >
                Status
              </SortableHeader>
            </div>
          </div>
        ),
        accessor: "category",
        Cell: ({ row }) => {
          const category = row.original;
          return (
            <CategoryAccordion
              category={category}
              layer={0}
              selectedIds={selectedIds}
              handleSelection={handleSelection}
            />
          );
        },
      },
    ];
  }, [flattenedCategories, selectedIds, order]);

  const DeactivateSelected = async () => {
    if (!selectedIds.length) {
      ToastError("Selecteer een categorie.");
      return;
    }

    // Collect all selected and their subcategory IDs
    let allIds = [...selectedIds];
    selectedIds.forEach(id => {
      const category = flattenedCategories.find(cat => cat.category_id === id);
      if (category) {
        allIds = allIds.concat(getAllSubCategoryIds(category));
      }
    });
    // Remove duplicates
    allIds = [...new Set(allIds)];

    const bulk_data = allIds.map(id => ({ category_id: id, status: 0 }));

    try {
      await middleware.put("products/category", { bulk_data });
      setSelectedIds([]);
      fetchAll();
    } catch (e) {
      console.error(e);
      ToastError("Er ging iets fout met het deactiveren.");
    }
  };

  const ActivateSelected = async () => {
    if (!selectedIds.length) {
      ToastError("Selecteer een categorie.");
      return;
    }
    // Collect all selected and their subcategory IDs
    let allIds = [...selectedIds];
    selectedIds.forEach(id => {
      const category = flattenedCategories.find(cat => cat.category_id === id);
      if (category) {
        allIds = allIds.concat(getAllSubCategoryIds(category));
      }
    });
    // Remove duplicates
    allIds = [...new Set(allIds)];

    const bulk_data = allIds.map(id => ({ category_id: id, status: 1 }));

    try {
      await middleware.put("products/category", { bulk_data });
      setSelectedIds([]);
      fetchAll();
    } catch (e) {
      console.error(e);
      ToastError("Er ging iets fout met het activeren.");
    }
  };

  const actions = (action) => {
    if (action === "deactivate") {
      DeactivateSelected();
    }
    if (action === "activate") {
      ActivateSelected();
    }
  };

  const tableInstance = useTable({ columns, data: categories ?? [] });

  const handleSearchChange = useCallback((value) => {
    setSearchengine(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  return (
    <PageTemplate
      pageIsLoading={loading}
      navbarTitle={"Categorieën Overzicht"}
    >
      <div className="w-100">
        <div>
          <BackButton
            text={'Terug'}
            onClickFunction={() => navigate('/algemene/instellingen/product')}
          />
        </div>

        <TablePreset
          data={[
            {
              selectedIds: selectedIds,
              tableInstance: tableInstance,
              pagination: pagination,
              primary_key: "category_id",
            },
          ]}
          handleSelection={handleSelection}
          setPagination={setPagination}
          searchBar={[{
            placeholder: 'Zoek op categorienaam of slug...',
            shown: true,
            searchFunction: handleSearchChange
          }]}
          actionOptions={[
            {
              title: "Categorieën archiveren",
              function: () => actions("deactivate"),
            },
            {
              title: "Categorieën publiceren",
              function: () => actions("activate"),
            },
          ]}
          leftButtons={[
            { title: "Filteren", function: () => setShowFilterModal(true) },
          ]}
          rightButtons={[
            {
              title: "Nieuwe categorie",
              function: () =>
                navigate("/algemene/instellingen/product/categorieën/toevoegen"),
            },
          ]}
        />
        <Filters
          filters={tempfilters}
          showFilterModal={showFilterModal}
          closeFilterModal={CloseFilterModal}
          setTempFilters={setTempFilters}
          setFilters={setFilters}
          defaultFilters={defaultFilters}
        />
      </div>
    </PageTemplate>
  );
};

export default CategorySettings;